import { Link } from "@/lib/router-compat";
import {
  CheckCircle2,
  ExternalLink,
  MapPin,
  Building2,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const points = [
  {
    text: "Verified SAINT® Provider — listed on Magnus Medical's official provider locator",
    link: "/find-a-verified-saint-provider",
  },
  {
    text: "Board-certified medical director & psychiatric clinical leadership",
    link: "/about",
  },
  { text: "Extended hours 7am–8pm and Saturdays", link: "" },
  {
    text: "Dedicated SAINT® Lounge Wing with private furnished suites for patient and family comfort",
    link: "",
  },
  {
    text: "On-site clinical care team ensuring comfort, dining, and personalized care between sessions",
    link: "",
  },
  {
    text: "Patients travel from all over the world for treatment because of our experience, privacy, and ability to accommodate their needs",
    link: "/traveling-for-treatment",
  },
  {
    text: "Dedicated travel coordinator for out-of-town patients",
    link: "/traveling-for-treatment",
  },
];

export const SaintWhyUs = () => (
  <section className="py-16 sm:py-20 bg-muted/30">
    <div className="container-px mx-auto max-w-6xl px-4">
      <div className="text-center mb-10">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-accent">
          <Building2 className="h-4 w-4" />
          Coral Springs, Florida Practice
        </span>
        <h2 className="mt-2 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-4xl">
          Why patients travel to us
        </h2>
        <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
          A dedicated outpatient neuromodulation center with private treatment
          suites, board-certified leadership, and comprehensive patient &amp;
          family care.
        </p>
      </div>

      <div className="grid gap-8 lg:grid-cols-12 lg:items-center mb-10">
        {/* Exterior Facility Photo Showcase */}
        <div className="lg:col-span-6 overflow-hidden rounded-2xl border border-border bg-card shadow-premium group">
          <div className="relative aspect-[16/9] w-full overflow-hidden bg-muted">
            <img
              src="https://vibe.filesafe.space/1787327131284421342/attachments/d2b0dcd1-7ac0-4ac9-ba1b-6c7fabe02e20.png"
              alt="Exterior view of Neuro Wellness TMS Centers of America at the Coral Springs Commerce Centre (9734 West Sample Road, 2nd Floor - Suite B, Coral Springs, FL 33065)"
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent pointer-events-none" />
            <div className="absolute bottom-3 left-3 right-3 text-white">
              <p className="font-headings text-sm sm:text-base font-bold flex items-center gap-1.5 drop-shadow">
                <MapPin className="h-4 w-4 text-accent shrink-0" />
                Coral Springs Commerce Centre · 9720–9734 W Sample Rd
              </p>
              <p className="text-xs text-white/85 drop-shadow">
                2nd Floor - Suite B · Coral Springs, FL 33065
              </p>
            </div>
          </div>
          <div className="p-5 sm:p-6 bg-card space-y-3">
            <p className="text-sm text-foreground/85 leading-relaxed">
              Located in beautiful Coral Springs, Florida, our private clinic is
              easily accessible from Fort Lauderdale (FLL), Palm Beach (PBI),
              and Miami (MIA) airports, with free on-site parking and accessible
              elevator entry.
            </p>
            <div className="pt-2 flex flex-wrap gap-3">
              <Button
                asChild
                size="sm"
                className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <Link
                  to="/find-a-verified-saint-provider"
                  className="inline-flex items-center gap-1.5"
                >
                  <span>View Verified Provider Spotlight</span>
                  <ArrowRight className="h-3.5 w-3.5" />
                </Link>
              </Button>
              <Button
                asChild
                size="sm"
                variant="outline"
                className="border-primary/20 text-primary hover:bg-muted"
              >
                <Link to="/about">About Our Practice</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Feature Points Grid */}
        <div className="lg:col-span-6 grid gap-3 sm:grid-cols-1">
          {points.map((item, idx) => (
            <div
              key={idx}
              className="flex items-start gap-3 rounded-xl border border-border bg-card p-4 shadow-soft"
            >
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-accent" />
              {item.link ? (
                item.link.startsWith("http") ? (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-foreground/85 leading-relaxed hover:text-accent underline underline-offset-2 decoration-accent/40"
                  >
                    {item.text}
                    <ExternalLink className="inline-block ml-1 h-3 w-3 text-accent" />
                  </a>
                ) : (
                  <Link
                    to={item.link}
                    className="text-sm text-foreground/85 leading-relaxed hover:text-accent underline underline-offset-2 decoration-accent/40 inline-flex items-center gap-1"
                  >
                    <span>{item.text}</span>
                    <ArrowRight className="inline-block h-3 w-3 text-accent shrink-0" />
                  </Link>
                )
              ) : (
                <span className="text-sm text-foreground/85 leading-relaxed">
                  {item.text}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);
